import LayoutWrapper from "../components/LayoutWrapper";
import HeaderBar from "../components/HeaderBar";
import { useState, useEffect, useContext, useMemo } from "react";
import { ThemeContext } from "../context/ThemeContext";
import getCurrentSkin from "../utils/getCurrentSkin";

import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import axios from "axios";
import { useLocation, useNavigate } from "react-router-dom";
import { FaEdit } from "react-icons/fa";
import skinColors from "../utils/skinColors";

const SupplierMainMasListing = () => {
  const { isDark, skin, setSkin } = useContext(ThemeContext);
  const skinColors = getCurrentSkin(skin);
  const { baseColor, baseShade, bgColor } = skinColors;
  const buttonClass = `bg-${baseColor}-${baseShade}`;
  const hoverClass = `hover:bg-${baseColor}-700`;

  const [suppliers, setSuppliers] = useState([]);
  const [selectedSuppliers, setSelectedSuppliers] = useState([]);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [supplierCode, setSupplierCode] = useState("");
  const [supplierName, setSupplierName] = useState("");
  const [loading, setLoading] = useState(false);
  const [isSearching, setIsSearching] = useState(false);

  const location = useLocation();
  const navigate = useNavigate();

  const finYearId = localStorage.getItem("finYearId") || "";
  const finYear = localStorage.getItem("finYear") || "";
  const branchId = localStorage.getItem("branchId") || "";
  const branch = localStorage.getItem("branchType") || "";
  const userId = localStorage.getItem("userId") || "";

  const commonHeaders = useMemo(
    () => ({
      "x-finyearid": finYearId,
      "x-finyear": finYear,
      "x-branchid": branchId,
      "x-branch": branch,
      "x-userid": userId,
    }),
    [finYearId, finYear, branchId, branch, userId]
  );

  const axiosConfig = useMemo(
    () => ({
      headers: commonHeaders,
    }),
    [commonHeaders]
  );

  const fetchSuppliers = async () => {
    setLoading(true);
    try {
      const res = await axios.get("https://nissi-india.com/api/supplierMaster_list", {
        params: { page, pageSize },
        headers: commonHeaders,
      });

      setSuppliers(res.data?.supplierList || []);
      setTotalCount(res.data?.totalRecords || 0);
      setTotalPages(res.data?.totalPages || 0);
    } catch (err) {
      toast.error("❌ Failed to fetch supplier list");
    } finally {
      setLoading(false);
    }
  };

  const searchSuppliers = async () => {
    if (!supplierCode && !supplierName) {
      toast.warning("⚠️ Enter Supplier Code or Name to search");
      return;
    }

    setLoading(true);
    setIsSearching(true);

    try {
      const res = await axios.get("https://nissi-india.com/api/supplierMaster_search", {
        params: {
          suppliercode: supplierCode,
          suppliername: supplierName,
        },
        headers: commonHeaders,
      });

      setSuppliers(res.data?.supplierListSearch || []);
      setTotalPages(1);
      setPage(1);
    } catch (err) {
      toast.error("❌ Failed to search suppliers");
    } finally {
      setLoading(false);
    }
  };

  const clearSearch = () => {
    setSupplierCode("");
    setSupplierName("");
    setPage(1);
    setIsSearching(false);
    fetchSuppliers();
  };

  const handleDeleteSelected = async () => {
    if (selectedSuppliers.length === 0) return;
    if (!window.confirm(`Are you sure you want to delete ${selectedSuppliers.length} supplier(s)?`)) return;

    try {
      const res = await axios.post(
        "https://nissi-india.com/api/supplierGroups_delete/delete",
        {
          groupNames: selectedSuppliers.map((s) => s.fld_suppliername),
        },
        {
          headers: commonHeaders,
        }
      );

      if (res.data.success) {
        toast.success("✅ Suppliers deleted successfully");
        setSelectedSuppliers([]);
        isSearching ? searchSuppliers() : fetchSuppliers();
      } else {
        toast.error(`❌ Failed to delete: ${res.data.message || "Unknown error"}`);
      }
    } catch (err) {
      toast.error("❌ Error deleting suppliers");
    }
  };

  const handleCheckboxChange = (supplier) => {
    setSelectedSuppliers((prev) => {
      const exists = prev.find((s) => s.fld_suppliercode === supplier.fld_suppliercode);
      return exists
        ? prev.filter((s) => s.fld_suppliercode !== supplier.fld_suppliercode)
        : [...prev, supplier];
    });
  };

  useEffect(() => {
    if (!isSearching) {
      fetchSuppliers();
    }
  }, [page]);

  return (
    <LayoutWrapper>
  <HeaderBar title="Supplier Listing" setSkin={setSkin} currentSkin={skin} />

  {/* Enhanced Container */}
  <div className={`m-6 rounded-3xl overflow-hidden shadow-2xl border transition-all duration-300 ${isDark ? "bg-gray-900 text-white border-gray-700" : "bg-white text-gray-900 border-gray-200"}`}>

    {/* Filter/Search Bar */}
    <div className="p-6 border-b border-gray-200 dark:border-gray-700 flex flex-wrap gap-4 items-center">
      <input
        type="text"
        placeholder="Supplier Code"
        value={supplierCode}
        onChange={(e) => setSupplierCode(e.target.value)}
        className="px-4 py-2 rounded-xl border w-60 dark:bg-gray-800 dark:border-gray-600"
      />
      <input
        type="text"
        placeholder="Supplier Name"
        value={supplierName}
        onChange={(e) => setSupplierName(e.target.value)}
        className="px-4 py-2 rounded-xl border w-60 dark:bg-gray-800 dark:border-gray-600"
      />
      <button className={`px-6 py-2 rounded-xl text-white ${buttonClass} ${hoverClass}`} onClick={searchSuppliers}>
        🔍 Search
      </button>
      <button className="bg-gray-500 text-white px-6 py-2 rounded-xl" onClick={clearSearch}>
        Reset
      </button>
      <button className="ml-auto bg-green-600 text-white px-6 py-2 rounded-xl" onClick={() => navigate("/inventory/Supplier-MainMas")}>
        ➕ Add New
      </button>
    </div>

    {/* Table Section */}
    <div className="overflow-auto">
      <table className="min-w-full text-sm text-left border-collapse">
        <thead className={`bg-${baseColor}-${baseShade} text-white`}>
          <tr>
            {["S.No", "Code", "Name", "State", "Zip", "Mobile", "Select", "Edit"].map((col, i) => (
              <th key={i} className="px-6 py-3 border-b border-white/10">{col}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {loading ? (
            <tr><td colSpan="8" className="p-6"><Skeleton count={5} height={30} /></td></tr>
          ) : suppliers.length === 0 ? (
            <tr>
              <td colSpan="8" className="text-center py-10 text-gray-500">📭 No suppliers found</td>
            </tr>
          ) : (
            suppliers.map((supplier, i) => (
              <tr key={supplier.fld_suppliercode} className="hover:bg-gray-100 dark:hover:bg-gray-800 border-t border-gray-200 dark:border-gray-700">
                <td className="px-6 py-4">{(page - 1) * pageSize + i + 1}</td>
                <td className="px-6 py-4">{supplier.fld_suppliercode}</td>
                <td className="px-6 py-4">{supplier.fld_suppliername}</td>
                <td className="px-6 py-4">{supplier.fld_statename}</td>
                <td className="px-6 py-4">{supplier.fld_ZipCode}</td>
                <td className="px-6 py-4">{supplier.fld_Mobile}</td>
                <td className="px-6 py-4 text-center">
                  <input
                    type="checkbox"
                    checked={selectedSuppliers.some((s) => s.fld_suppliercode === supplier.fld_suppliercode)}
                    onChange={() => handleCheckboxChange(supplier)}
                    className="w-5 h-5"
                  />
                </td>
                <td className="px-6 py-4 text-center">
                  <button
                    className="text-blue-600 hover:text-blue-800"
                    onClick={() => navigate("/inventory/Supplier-Main-MasEdit", { state: { supplier } })}
                  >
                    <FaEdit />
                  </button>
                </td>
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>

    {/* Pagination & Delete */}
    <div className="flex justify-between items-center p-6 border-t border-gray-200 dark:border-gray-700">
      <button
        className="bg-red-600 text-white px-6 py-2 rounded-xl disabled:opacity-50"
        onClick={handleDeleteSelected}
        disabled={selectedSuppliers.length === 0}
      >
        🗑️ Remove Selected
      </button>

      <div className="flex gap-2">
        <button
          onClick={() => setPage(p => Math.max(p - 1, 1))}
          disabled={page === 1}
          className="px-4 py-2 rounded-xl border"
        >
          ← Prev
        </button>
        {[...Array(totalPages)].map((_, idx) => (
          <button
            key={idx}
            onClick={() => setPage(idx + 1)}
            className={`px-4 py-2 rounded-xl border ${page === idx + 1 ? `bg-${baseColor}-${baseShade} text-white` : ""}`}
          >
            {idx + 1}
          </button>
        ))}
        <button
          onClick={() => setPage(p => Math.min(p + 1, totalPages))}
          disabled={page === totalPages}
          className="px-4 py-2 rounded-xl border"
        >
          Next →
        </button>
      </div>
    </div>
  </div>

  <ToastContainer position="top-right" autoClose={3000} theme={isDark ? "dark" : "light"} />
</LayoutWrapper>

  );
};

export default SupplierMainMasListing;
